import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Skeleton,
  Space,
  Button,
  message,
  Tabs,
  Alert,
  Input,
  InputNumber,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import {
  getWorkflow,
  updateWorkflowPrompts,
  updateWorkflowDefinition,
  type WorkflowTemplate,
} from '@/api/workflows';
import { formatDateTime } from '@/utils/format';

export function WorkflowDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [wf, setWf] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    getWorkflow(decodeURIComponent(code))
      .then(setWf)
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/workflows')}>
          返回列表
        </Button>
      </Space>

      {loading || !wf ? (
        <Skeleton active />
      ) : (
        <Card>
          <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Code">{wf.code}</Descriptions.Item>
            <Descriptions.Item label="名称">{wf.name}</Descriptions.Item>
            <Descriptions.Item label="类型">
              <Tag color="purple">{wf.kind}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="版本">v{wf.version}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag>{wf.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="消耗钻石">{wf.cost_diamonds ?? 0}</Descriptions.Item>
            <Descriptions.Item label="末端换脸">
              {wf.final_face_swap ? '是' : '否'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {formatDateTime(wf.updated_at)}
            </Descriptions.Item>
            <Descriptions.Item label="provider chain" span={2}>
              <Space>
                {(wf.provider_chain || []).map((p) => (
                  <Tag key={p}>{p}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          </Descriptions>

          <Tabs
            type="card"
            items={[
              {
                key: 'pricing',
                label: '定价',
                children: <PricingEditor wf={wf} onSaved={setWf} />,
              },
              {
                key: 'prompts',
                label: 'LLM Prompts',
                children: <PromptsEditor wf={wf} onSaved={setWf} />,
              },
              {
                key: 'def',
                label: 'definition',
                children: <JsonBlock value={wf.definition} />,
              },
              {
                key: 'in',
                label: 'input_schema',
                children: <JsonBlock value={wf.input_schema} />,
              },
              {
                key: 'out',
                label: 'output_schema',
                children: <JsonBlock value={wf.output_schema} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * PricingEditor — 运营自助编辑工作流定价 (创意工具/套系)。落到 definition JSONB:
 *  - cost_diamonds: 生成时扣钻 (创意工具一般 0 = 生成免费)
 *  - unlock_diamonds: 解锁成品扣钻 (#4 机制: 免费生成→打码→付钻解锁)
 * 保存即 PATCH /admin/v1/workflows/:code {definition}, 合并进现有 definition;
 * worker 60s 内 cache 失效后生效 (定价在下单/解锁时读 template definition)。
 */
function PricingEditor({
  wf,
  onSaved,
}: {
  wf: WorkflowTemplate;
  onSaved: (next: WorkflowTemplate) => void;
}) {
  const def = (wf.definition || {}) as Record<string, unknown>;
  const [cost, setCost] = useState<number>(Number(def.cost_diamonds ?? 0));
  const [unlock, setUnlock] = useState<number>(Number(def.unlock_diamonds ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const d = (wf.definition || {}) as Record<string, unknown>;
    setCost(Number(d.cost_diamonds ?? 0));
    setUnlock(Number(d.unlock_diamonds ?? 0));
  }, [wf.code]);

  const onSave = async () => {
    setSaving(true);
    try {
      const merged = {
        ...((wf.definition || {}) as Record<string, unknown>),
        cost_diamonds: cost,
        unlock_diamonds: unlock,
      };
      const next = await updateWorkflowDefinition(wf.code, merged);
      onSaved(next);
      message.success('定价已保存 — worker 60s 内生效');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="创意工具/套系定价。生成消耗=下单时扣钻(创意工具一般填 0=生成免费); 解锁价=查看/下载成品时扣钻(免费生成→打码→付钻解锁)。改后 worker 60s 内生效。"
      />
      <Space size="large">
        <div>
          <div style={{ marginBottom: 4, color: '#666' }}>生成消耗 (cost_diamonds)</div>
          <InputNumber min={0} value={cost} onChange={(v) => setCost(Number(v ?? 0))} style={{ width: 160 }} addonAfter="钻" />
        </div>
        <div>
          <div style={{ marginBottom: 4, color: '#666' }}>解锁价 (unlock_diamonds)</div>
          <InputNumber min={0} value={unlock} onChange={(v) => setUnlock(Number(v ?? 0))} style={{ width: 160 }} addonAfter="钻" />
        </div>
      </Space>
      <div>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
          保存定价
        </Button>
      </div>
    </Space>
  );
}

/**
 * PromptsEditor — content / 运营自助编辑 LLM system prompt. 落到
 * workflow_templates.prompts JSONB; worker 60s 内 cache 失效后生效.
 *
 * 设计:
 *  - 每个 key (system / story / ...) 一个 TextArea
 *  - 加新 key 按钮 (未来 App 可能引入 thumb_caption / share_caption 等)
 *  - 保存即 PATCH /admin/v1/workflows/:code, 只送 {prompts} 字段
 *  - 提示: prompts 为空时 worker fallback 到 binary embed
 */
function PromptsEditor({
  wf,
  onSaved,
}: {
  wf: WorkflowTemplate;
  onSaved: (next: WorkflowTemplate) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(wf.prompts || {});
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);

  // 若上游 wf 变 (e.g. 切了 record), 重置 draft
  useEffect(() => {
    setDraft(wf.prompts || {});
  }, [wf.code]);

  const onSave = async () => {
    setSaving(true);
    try {
      const next = await updateWorkflowPrompts(wf.code, draft);
      onSaved(next);
      message.success('保存成功 — worker 60s 内生效');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const onAdd = () => {
    const k = newKey.trim();
    if (!k) return;
    if (draft[k] !== undefined) {
      message.warning(`键 "${k}" 已存在`);
      return;
    }
    setDraft({ ...draft, [k]: '' });
    setNewKey('');
  };

  const onChange = (key: string, val: string) => {
    setDraft({ ...draft, [key]: val });
  };

  const onRemove = (key: string) => {
    const next = { ...draft };
    delete next[key];
    setDraft(next);
  };

  const keys = Object.keys(draft);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="编辑系统级 LLM prompt — 不需要重启服务, 60s 内 worker 自动拉取。空字段会回退到内置 embed。"
      />

      {keys.length === 0 && (
        <Alert
          type="warning"
          message="当前 workflow 还没有任何 prompt。worker 会用 binary embed 作为回退。下面加一个 key (system / story) 开始编辑。"
        />
      )}

      {keys.map((k) => (
        <Card
          key={k}
          size="small"
          title={
            <Space>
              <Tag color="blue">{k}</Tag>
              <span style={{ color: '#999', fontSize: 12 }}>
                {draft[k] ? `${draft[k].length} chars` : '(空 — 回退 embed)'}
              </span>
            </Space>
          }
          extra={
            <Button danger size="small" onClick={() => onRemove(k)}>
              移除
            </Button>
          }
        >
          <Input.TextArea
            value={draft[k]}
            onChange={(e) => onChange(k, e.target.value)}
            autoSize={{ minRows: 8, maxRows: 30 }}
            style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
            placeholder={`${k} 系统 prompt — 留空回退 binary embed`}
          />
        </Card>
      ))}

      <Space>
        <Input
          placeholder="新 prompt key (e.g. system / story / thumb_caption)"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          style={{ width: 320 }}
          onPressEnter={onAdd}
        />
        <Button onClick={onAdd}>添加</Button>
      </Space>

      <div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={onSave}
        >
          保存到 workflow_templates.prompts
        </Button>
      </div>
    </Space>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre
      style={{
        background: '#f8fafc',
        padding: 12,
        borderRadius: 6,
        maxHeight: 400,
        overflow: 'auto',
        fontSize: 12,
        margin: 0,
      }}
    >
      {value ? JSON.stringify(value, null, 2) : '(empty)'}
    </pre>
  );
}
